import { SUBSCRIPTION_PLANS } from '@/constants/subscription'
import { useSubscriptions } from '@/hooks/useSubscriptions'
import { STRIPE_INTERVAL } from '@/constants/subscription'
import { SubscriptionType } from '@/types/subscription'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useMemo } from 'react'

export function SubscriptionPage() {
  const { t } = useTranslation()
  const { subscriptions, isLoading: subscriptionsLoading } = useSubscriptions()

  const lowestIndividualMonthlyPrice = useMemo(() => {
    const monthlyIndividual = subscriptions.filter(
      sub => sub.type === SubscriptionType.INDIVIDUAL && sub.interval === STRIPE_INTERVAL.MONTH
    )
    if (monthlyIndividual.length === 0) return null
    const minCents = Math.min(...monthlyIndividual.map(sub => sub.price.amount))
    return Math.round(minCents / 100)
  }, [subscriptions])

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <section className="bg-secondary shrink-0">
        <div className="mx-auto max-w-360 px-4 py-12 text-center">
          <h1 className="text-foreground mb-2 text-2xl font-semibold">
            {t('subscription.hero.title')}
          </h1>
          <p className="text-foreground mx-auto max-w-160 text-base font-normal">
            {t('subscription.hero.description')}
          </p>
        </div>
      </section>

      <section className="bg-background flex min-h-0 flex-1 items-center justify-center">
        <div className="mx-auto w-full max-w-360 px-4 py-12">
          <div className="flex flex-wrap justify-center gap-10">
            {SUBSCRIPTION_PLANS.map(plan => (
              <div
                key={plan.id}
                className="flex max-w-79 flex-col rounded-xl border border-[#EBEBEB] bg-white px-4 py-6 text-center shadow-[0px_3.39px_7.63px_0px_rgba(0,0,0,0.05)]"
              >
                <h2 className="text-foreground mb-1.5 text-2xl font-semibold">
                  {t(plan.titleKey)}
                </h2>
                <p className="flex-1 text-sm font-normal text-[#484848]">
                  {t(plan.descriptionKey)}
                </p>
                {plan.id === SubscriptionType.INDIVIDUAL &&
                  (subscriptionsLoading || lowestIndividualMonthlyPrice !== null) && (
                    <div className="mx-auto mt-4 mb-7 flex w-53.5 max-w-57 items-center justify-center gap-4 rounded-lg bg-[#F5F5F5] px-5 py-[8.5px]">
                      <span className="text-xs font-normal text-[#484848]">
                        {t('subscription.plans.structure.pricingNote.label')}
                      </span>
                      {subscriptionsLoading ? (
                        <span
                          className="animate-shimmer inline-block h-4 w-12 rounded bg-linear-to-r from-[#E0E0E0] via-[#F0F0F0] to-[#E0E0E0] bg-size-[200%_100%]"
                          aria-hidden
                        />
                      ) : (
                        <span className="text-foreground text-base font-bold">
                          {lowestIndividualMonthlyPrice}€
                        </span>
                      )}
                    </div>
                  )}
                {'pricingNote' in plan && plan.pricingNote && (
                  <div className="mx-auto mt-4 mb-7 flex max-w-57 items-center gap-4 rounded-lg bg-[#F5F5F5] px-5 py-[8.5px]">
                    <span className="text-xs font-normal text-[#484848]">
                      {t(plan.pricingNote.labelKey)}
                    </span>
                    <span className="text-foreground text-base font-bold">
                      {t(plan.pricingNote.valueKey)}
                    </span>
                  </div>
                )}
                <Link
                  to={`/subscribe/${plan.id}`}
                  className="bg-primary text-primary-foreground rounded-md px-10 py-2.5 text-base font-medium transition-opacity hover:opacity-90"
                >
                  {t('subscription.plans.subscribeButton')}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
